import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface PlacePort {
  id: string;
  bateau_id: string;
  user_id: string;
  annee: number;
  cout: number;
  nom_port: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const usePlacesPort = (bateauId?: string) => {
  const { user } = useAuth();
  const [placesPort, setPlacesPort] = useState<PlacePort[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPlacesPort = useCallback(async () => {
    if (!user || !bateauId) return;
    
    setLoading(true);
    try {
      const { data, error } = await (supabase
        .from('places_port' as any)
        .select('*')
        .eq('bateau_id', bateauId)
        .order('annee', { ascending: false }) as any);

      if (error) throw error;
      setPlacesPort((data || []) as PlacePort[]);
    } catch (error: any) {
      console.error('Erreur lors du chargement des places port:', error);
    } finally {
      setLoading(false);
    }
  }, [user, bateauId]);

  useEffect(() => {
    fetchPlacesPort();
  }, [fetchPlacesPort]);

  const getPlacePortParAnnee = useCallback((annee: number) => {
    return placesPort.find(p => p.annee === annee) || null;
  }, [placesPort]);

  const ajouterPlacePort = async (data: Omit<PlacePort, 'id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: 'Non authentifié' };

    try {
      const { error } = await (supabase
        .from('places_port' as any)
        .insert({
          ...data,
          user_id: user.id,
        }) as any);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Coût de la place au port enregistré",
      });

      await fetchPlacesPort();
      return { error: null };
    } catch (error: any) {
      console.error('Erreur lors de l\'ajout:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer le coût",
        variant: "destructive",
      });
      return { error: error.message };
    }
  };

  const modifierPlacePort = async (id: string, data: Partial<PlacePort>) => {
    if (!user) return { error: 'Non authentifié' };

    try {
      const { error } = await (supabase
        .from('places_port' as any)
        .update(data)
        .eq('id', id) as any);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Coût de la place au port modifié",
      });

      await fetchPlacesPort();
      return { error: null };
    } catch (error: any) {
      console.error('Erreur lors de la modification:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le coût",
        variant: "destructive",
      });
      return { error: error.message };
    }
  };

  return {
    placesPort,
    loading,
    ajouterPlacePort,
    modifierPlacePort,
    getPlacePortParAnnee,
    refetch: fetchPlacesPort,
  };
};
